/*jshint esversion: 6 */

import {t} from "./i18n.js";

const Util = {
    confirm: (callback, options) => {
        "use strict";
        if (typeof callback !== "function") {
            return;
        }
        if (window.shiftPressed) {
            callback();
            return;
        }

        let defaultOptions = {
            title: t("Confirmação"),
            message: t("Você tem certeza que deseja continuar?"),
            confirmText: t("Sim"),
            cancelText: t("Cancelar"),
        };

        options = Object.assign(defaultOptions, options);

        window.dispatchEvent(
            new CustomEvent("confirmation", {
                detail: {
                    callback: callback,
                    title: options.title,
                    message: options.message,
                    confirmText: options.confirmText,
                    cancelText: options.cancelText,
                },
            })
        );
    },
    copyToClipboard: (text) => {
        "use strict";
        const done = () => Util.notify(t("Copiado para a área de transferência"));
        const fallback = () => {
            // O textarea precisa ficar DENTRO do modal aberto (focus trap): anexado ao body
            // ele não recebe o foco e o execCommand("copy") falha em silêncio.
            const host = document.activeElement?.closest?.("[x-data]") ?? document.body;
            const el = document.createElement("textarea");
            el.value = text;
            el.setAttribute("readonly", "");
            el.style.cssText = "position:fixed;top:0;left:0;opacity:0;";
            host.appendChild(el);
            el.focus();
            el.select();
            let ok = false;
            try {
                ok = document.execCommand("copy");
            } catch (e) {
                ok = false;
            }
            host.removeChild(el);
            ok ? done() : Util.notify(t("Não foi possível copiar. Selecione e copie manualmente."), "error");
        };

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(text).then(done, fallback);
        } else {
            fallback();
        }
    },
    notify: (message, type = "success") => {
        window.dispatchEvent(new CustomEvent("notify", {
            detail: {
                type: type,
                title: message
            }
        }));
    },
    animateClick: (el, animation, time = 1500) => {
        el.classList.remove(animation);
        el.classList.add("animate__animated", animation);
        setTimeout(() => {
            el.classList.remove("animate__animated", animation);
        }, time);
    },
    animateContent: (el, text, time = 500) => {
        el.dataset.text = el.innerHTML;
        el.setAttribute("disabled", true);
        el.classList.add("animate__animated", "animate__faster", "animate__fadeOut");
        setTimeout(() => {
            el.innerHTML = text;
            el.classList.remove("animate__animated", "animate__faster", "animate__fadeOut");
            el.classList.add("animate__animated", "animate__faster", "animate__fadeIn");
            setTimeout(() => {
                el.classList.remove("animate__animated", "animate__faster", "animate__fadeIn");
            }, 500);
        }, 500);
        setTimeout(() => {
            el.innerHTML = el.dataset.text;
            el.removeAttribute("disabled");
        }, time + 1200);
    },
    debounce: (func, wait) => {
        "use strict";
        let timeout;
        return function () {
            const context = this, args = arguments;
            const later = function () {
                timeout = null;
                func.apply(context, args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    },
    formatMoney: (subject) => {
        "use strict";
        return (
            "R$ " +
            subject
                .toFixed(2)
                .replace(".", ",")
                .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.")
        );
    },
    maxPasswordLevel: 4,
    moneyMask: (value) => {
        "use strict";
        if (!value) {
            return value;
        }
        value = value.replace(/\D/g, "");
        value = value.replace(/(\d{1,2})$/, ",$1");
        value = value.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
        // x-mask:dynamic espera um template (9 = dígito), não o valor:
        // devolver "2,90" fazia o 2 e o 0 virarem literais fixos.
        return value.replace(/\d/g, "9");
    },
    scorePassword: (pass) => {
        "use strict";
        let score = 0;
        if (!pass) {
            return score;
        }

        if (pass.length >= 8) {
            score += 1;
        }

        // check uppercase
        if (/[A-Z]/.test(pass)) {
            score += 1;
        }

        // check numbers
        if (/\d/.test(pass)) {
            score += 1;
        }

        return parseInt(score);
    },
    getUserLocation: (callback) => {
        "use strict";
        navigator.geolocation.getCurrentPosition((position) => {
            // check if location is already saved and is not expired
            let location = JSON.parse(localStorage.getItem('location'));
            if (location) {
                let now = new Date().getTime();
                let diff = now - location.timestamp;
                if (diff < 1000 * 60 * 60 * 24) {
                    return;
                }
            }

            let csrf = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
            fetch('/user/location', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrf
                },
                body: JSON.stringify({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                })
            }).then(r => r.json()).then(r => {
                if (r.success) {
                    localStorage.setItem('location', JSON.stringify({
                        timestamp: new Date().getTime()
                    }));

                    if (callback) {
                        callback(r);
                    }
                }
            }).catch(e => {
                console.error(e);
            });
        });
    },

    downloadSVGAsPNG: (base64Svg, outputFileName = "qrcode.png") => {
        const svgData = atob(base64Svg.replace(/^data:image\/svg\+xml;base64,/, ""));
        const svgBlob = new Blob([svgData], {type: "image/svg+xml"});
        const url = URL.createObjectURL(svgBlob);

        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;

            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);

            canvas.toBlob((blob) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = outputFileName;
                a.click();
                URL.revokeObjectURL(url);
            }, "image/png");
        };
        img.src = url;
    },

    downloadSVGAsSVG: (base64Svg, outputFileName = "imagem.svg") => {
        const svgData = atob(base64Svg.replace(/^data:image\/svg\+xml;base64,/, ""));
        const blob = new Blob([svgData], {type: "image/svg+xml"});

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = outputFileName;
        a.click();
        URL.revokeObjectURL(url);
    }

};
window.Util = Util;
